import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InsuranceAmountInput } from 'src/app/_shared/interface/deal-analysis.interface';
import { ToNumberPipe } from 'src/app/_shared/pipe/to-number.pipe';
import { DealAnalysisService } from 'src/app/_shared/service/deal-analysis.service';
import { ConfirmDialogComponent } from 'src/app/shared/modules/alerts/confirm-dialog/confirm-dialog.component';
import { MyHomeState } from '../../state/my-home.state';

@Component({
  selector: 'app-rental-deal-analysis-card',
  templateUrl: './rental-deal-analysis-card.component.html',
  styleUrls: ['./rental-deal-analysis-card.component.scss'],
})
export class RentalDealAnalysisCardComponent implements OnChanges {
  onDestroyNotifier$ = new Subject();
  hoaFeeValue = 0;
  propertyTaxValue = 0;
  insuranceValue = 0;
  mortgagePaymentMonthly = 0;
  totalPaymentMonthly: number;
  potentialMonthlyRent: number;
  loggerAddress: any;
  pmxPropertyId;
  monthlyRent = 0;

  @Input() savedDealData: any;
  cashFlow: any;

  // Subject used to unsubscribe all Subscriptions on OnDestroy
  private onDestroyNotifier: Subject<any> = new Subject<any>();
  inuranceAmount: any;
  operatingExpense: any;

  constructor(
    private myHomeState: MyHomeState,
    private toNumberPipe: ToNumberPipe,
    private dealAnalysisService: DealAnalysisService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    // this.myHomeState.activePropertyDetails$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
    //   this.propertyTaxValue = res?.basic?.TaxAnnualAmount
    //     ? this.toNumberPipe.transform(res?.basic?.TaxAnnualAmount) / 12
    //     : 0;
    // });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['savedDealData']) return;

    const currentData = changes['savedDealData']?.currentValue;
    if (!currentData?.computationInput) return;
    let computationInput = currentData.computationInput;

    // Extract key values with default fallbacks
    const loanInterestRate = computationInput.uInterestRate || 0;
    const loanAmount = computationInput.uLoanAmount || 0;
    const loanTerm = computationInput.uLoanTerm || 0;
    this.hoaFeeValue = computationInput.uHoaFee || 0;
    const monthlyRental = computationInput.uMonthlyRent || 0;
    this.propertyTaxValue = (computationInput.uPropertyTaxAmount || 0) / 12;
    this.insuranceValue = (computationInput.uInsuranceAmount || 0) / 12;

    // Compute ratios
    computationInput = {
      ...computationInput,
      uYear: 1,
      uMonthlyRentYoYRatio: (computationInput.uMonthlyRentYoYPercent || 0) / 100,
      uRentChurnRatio: (computationInput.uRenterChurn || 0) / 100,
      uPropertyManagementRatio: (computationInput.uPropertyManagementPercent || 0) / 100,
      uMaintenanceRatio: (computationInput.uMaintenancePercent || 0) / 100,
      uWarrantyServiceYoYRatio: (computationInput.uWarrantyServiceYoYPercent || 0) / 100,
      uAccountingYoYRatio: (computationInput.uAccountingYoYPercent || 0) / 100,
    };

    // Compute Monthly Mortgage Payment
    const { monthlyMortgage } = this.dealAnalysisService.computeMonthlyMortgagePayment({
      uInterestRate: loanInterestRate / 100,
      uLoanAmount: loanAmount,
      uLoanTerm: loanTerm,
    });

    this.mortgagePaymentMonthly = isFinite(monthlyMortgage) ? Math.round(Number(monthlyMortgage)) : 0;
    this.totalPaymentMonthly =
      this.mortgagePaymentMonthly + this.propertyTaxValue + this.hoaFeeValue + this.insuranceValue;
    this.monthlyRent = monthlyRental;

    // Compute Cash Flow Components
    const grossRent = this.dealAnalysisService.computeGrossRent(computationInput);
    const vacancy = this.dealAnalysisService.computeVacancy(computationInput);
    const warrantyServiceCost = this.dealAnalysisService.computeWarrantyServiceCost(computationInput);
    const accountingCost = this.dealAnalysisService.computeAccountingCost(computationInput);

    const operatingIncome = grossRent.grossRent + vacancy.vacancy;
    const otherExpenses = operatingIncome * ((computationInput.uOtherExpenses || 0) / 100);
    const propertyManagement = operatingIncome * computationInput.uPropertyManagementRatio;
    const propertyMaintenance = grossRent.grossRent * computationInput.uMaintenanceRatio;


    // Compute Operating Expense based on mode
    if (currentData.isAdvancedMode) {

      this.operatingExpense =
        computationInput.uPropertyTaxAmount +
        computationInput.uInsuranceAmount +
        computationInput.uHoaFee +
        propertyManagement +
        propertyMaintenance +
        warrantyServiceCost.warrantyService +
        accountingCost.accounting;
    } else {

      this.operatingExpense = computationInput.uPropertyTaxAmount + otherExpenses;
    }

    // Compute Net Operating Income and Cash Flow
    const netOperatingIncome = operatingIncome - this.operatingExpense;
    this.cashFlow = (netOperatingIncome - this.mortgagePaymentMonthly * 12) / 12;

    // Trigger Change Detection
    this.cdr.detectChanges();
  }

  isNegativeOrder(value: number): boolean {
    return value < 0;
  }

  getInsuranceAmount(uFairMarketValue, uLandValue, uState, uPurchasePrice): number {
    if (uLandValue < uPurchasePrice) {
      const insuranceAmountInput: InsuranceAmountInput = {
        uInsuranceAmount: 0,
        uLandValue: uLandValue,
        uFairMarketValue: uFairMarketValue,
        uFairMarketValueYoYRatio: 0,
        uState: uState,
        uYear: 1,
        uPurchasePrice: uPurchasePrice,
      };
      const { insurance } = this.dealAnalysisService.computeInsuranceAmount(insuranceAmountInput);
      return insurance / 12;
    } else {
      let insurance = 0;
      return insurance;
    }
  }

  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  actionTrigger(action) {
    //   if (action == 'copy') {
    //     this.triggeredActionCopy.emit(action);
    //   } else if (action == 'move') {
    //     this.triggeredActionMove.emit(action);
    //   } else if (action == 'delete') {
    //     this.triggeredActionDelete.emit(action);
    //   }
  }

  navigateToSavedDeal(dealId, dealType) {
    if (dealId && dealType) {
      this.router.navigate(['my-property'], {
        queryParams: { 'deal-analysis-id': dealId, rentalAnalysis: true },
      });
    } else if (dealId) {
      this.router.navigate(['my-property'], {
        queryParams: { 'deal-analysis-id': dealId, rentalAnalysis: false },
      });
    }
  }

  deleteDeal(dealId, deleteBy, PMXPropertyId, dealType) {
    this.openDeleteDealDialog(dealId, deleteBy, PMXPropertyId, dealType);
  }

  openDeleteDealDialog(dealId, deleteBy, PMXPropertyId, dealType) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      data: {
        type: 'deleteDeal',
        title: 'Do you want to delete?',
        proceedAction: (status, callBack) => {
          let params: any = {
            id: dealId,
          };
          if (dealType) {
            params.type = dealType;
          }
          if (status) {
            this.dealAnalysisService
              .deleteDeal(params)
              .pipe(takeUntil(this.onDestroyNotifier))
              .subscribe((res) => {
                this.dealAnalysisService
                  .getSavedDealAnalysisByAddress({ PMXPropertyId: PMXPropertyId })
                  .pipe(takeUntil(this.onDestroyNotifier))
                  .subscribe((res: any) => {
                    if (res && res?.savedDeals && res?.savedDeals.length) {
                      this.savedDealData = res?.savedDeals[0]?.deals;
                      if (this.savedDealData.length) {
                        this.myHomeState.rentalDealsListValue = this.savedDealData;
                      } else {
                        this.myHomeState.rentalDealsListValue = [];
                      }
                    } else {
                      this.myHomeState.rentalDealsListValue = [];
                    }
                  });
              });
            callBack({ status: true });
            dialogRef.close();
          } else {
            return false;
          }
        },
        proceedCancelAction: () => {
          dialogRef.close();
        },
      },
    });
  }
}
